import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Camera, CircleArrowLeft, LoaderCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router";

import { ClubBanner } from "@/components/banner/club-banner";
import { ClubInformations } from "@/components/club/club-informations";
import { ClubMatchOffers } from "@/components/club/club-match-offers";
import { ClubSpecs } from "@/components/club/club-specs";
import { ClubTeams } from "@/components/club/club-teams";
import { ClubTournamentCard } from "@/components/club/club-tournament-card";
import { ProfileSkeleton } from "@/components/skeleton/profile-skeleton";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useMe } from "@/context/user-context";
import { useToast } from "@/hooks/use-toast";
import { useTRPC } from "@/lib/trpc";
import { UploadButton } from "@/lib/uploadthing";
import { routes } from "@/routes";

export function ClubProfile() {
  const { id } = useParams();
  const { user } = useMe();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { data, status } = useQuery(trpc.club.getClub.queryOptions({ clubId: id || user.id }));
  const { t } = useTranslation(["common", "tournaments", "profile"]);
  const location = useLocation();
  const fromPath = location.state?.fromPath;
  const navigate = useNavigate();
  const { toast } = useToast();

  const { mutate } = useMutation(trpc.club.updateClubPublicStatus.mutationOptions({
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
      toast({
        title: t("common:change-saved"),
        description:
        variables.isPublic
          ? "Le club est maintenant visible dans la liste."
          : "Le club n'est plus visible dans la liste.",
      });
    },
    onError: () => {
      toast({
        title: t("common:error-occured"),
        description: "Une erreur est survenue, veuillez réessayer",
      });
    },
  }));

  if (status === "pending") {
    return <ProfileSkeleton />;
  }
  // Jamais de page blanche muette : l'erreur remonte à l'ErrorBoundary du Layout,
  // qui affiche un écran d'erreur avec rechargement (et la remonte à Sentry).
  if (status === "error") {
    throw new Error("Failed to fetch club");
  }

  return (
    <>
      {user.isAdmin && (
        <div className="m-2 flex items-center justify-between rounded-xl bg-white p-4 shadow-sm md:hidden">
          <span className="font-medium text-neutral-500">
            Le club est visible dans la liste
          </span>

          <Switch
            defaultChecked={data.isPublic}
            onCheckedChange={(event) => {
              mutate({
                clubIdToUpdate: data.clubRepresentativeId,
                isPublic: event,
              });
            }}
          />
        </div>
      )}

      { user.role === "club" && fromPath !== routes.admin && data.isNew && (
        <div className="container px-2 py-4 md:hidden">
          <ClubBanner />
        </div>
      )}

      <div className="relative h-[200px] w-full bg-orange-100 md:hidden">
        { fromPath && (
          <Button
            variant="outline"
            className="absolute left-1/2 top-4 w-fit -translate-x-1/2"
            onClick={() => navigate(-1)}
          >
            <CircleArrowLeft className="text-orange-500" />
            {fromPath === routes.favorites && t("common:favorites-list")}
            {fromPath === routes.clubs && t("common:clubs-list")}
            {fromPath === routes.tournaments && t("common:back-to-tournament")}
          </Button>
        )}

        {data.coverImgUrl && (
          <img
            src={data.coverImgUrl}
            alt={`Profil de ${data.clubRepresentativeFirstName}`}
            className="pointer-events-none size-full select-none object-cover"
          />
        )}
        {(user.role === "club" || user.isAdmin) && (
          <UploadButton
            endpoint="imageUploader"
            onBeforeUploadBegin={(files) => {
              const newF = files.map(
                f => new File([f], `club-cover-${data.id}`, { type: f.type }),
              );
              return newF;
            }}
            onClientUploadComplete={() => {
              queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
              toast({
                title: t("common:change-saved"),
                description: t("common:cover-img-success"),
              });
            }}
            onUploadError={(error: Error) => {
              toast({
                title: t("common:change-error"),
                description: (
                  <span>
                    {error.message.includes("FileSizeMismatch") ? t("common:cover-img-error-size") : error.message}
                  </span>
                ),
              });
            }}
            input={{
              clubId: data.clubRepresentativeId,
            }}
            appearance={{
              container: "border border-neutral-400 bg-white w-fit rounded-lg absolute bottom-2 right-2",
              button: "bg-transparent px-2 py-1.5",
              allowedContent: "hidden",
            }}
            content={{
              button({ isUploading }) {
                return (
                  <div className="flex items-center gap-1">
                    {isUploading ? <LoaderCircle className="size-4 animate-spin text-orange-500" /> : <Camera className="size-4 text-orange-500" />}
                    <span className="text-xs text-neutral-500">Image 4MB</span>
                  </div>
                );
              },
            }}
          />
        )}
      </div>

      <div className="container flex flex-col gap-4 px-2 py-4 md:p-4">
        { user.role === "club" && fromPath !== routes.admin && (
          <div className="hidden md:block">
            <ClubBanner />
          </div>
        )}

        { fromPath && (
          <Button
            variant="outline"
            className="hidden w-fit md:inline-flex"
            onClick={() => navigate(-1)}
          >
            <CircleArrowLeft className="text-orange-500" />
            {fromPath === routes.favorites && t("common:favorites-list")}
            {fromPath === routes.clubs && t("common:clubs-list")}
            {fromPath === routes.tournaments && t("common:back-to-tournament")}
          </Button>
        )}

        {user.isAdmin && (
          <div className="hidden items-center justify-between rounded-xl bg-white p-4 shadow-sm md:flex">
            <span className="font-medium text-neutral-500">
              Le club est visible dans la liste
            </span>

            <Switch
              defaultChecked={data.isPublic}
              onCheckedChange={(event) => {
                mutate({
                  clubIdToUpdate: data.clubRepresentativeId,
                  isPublic: event,
                });
              }}
            />
          </div>
        )}

        <div className="z-10 rounded-[20px] bg-white shadow-sm">
          <div className="relative hidden h-[250px] w-full rounded-t-[20px] bg-orange-100 md:block">
            {data.coverImgUrl && (
              <img
                src={data.coverImgUrl}
                alt={`Profil de ${data.clubRepresentativeFirstName}`}
                className="pointer-events-none size-full select-none rounded-t-[20px] object-cover"
              />
            )}
            {(user.role === "club" || user.isAdmin) && (
              <UploadButton
                endpoint="imageUploader"
                onBeforeUploadBegin={(files) => {
                  const newF = files.map(
                    f => new File([f], `club-cover-${data.id}`, { type: f.type }),
                  );
                  return newF;
                }}
                onClientUploadComplete={() => {
                  queryClient.invalidateQueries(trpc.club.getClub.queryFilter());
                  toast({
                    title: t("common:change-saved"),
                    description: t("common:cover-img-success"),
                  });
                }}
                onUploadError={(error: Error) => {
                  toast({
                    title: t("common:change-error"),
                    description: (
                      <span>
                        {error.message.includes("FileSizeMismatch") ? t("common:cover-img-error-size") : error.message}
                      </span>
                    ),
                  });
                }}
                input={{
                  clubId: data.clubRepresentativeId,
                }}
                appearance={{
                  container: "border border-neutral-400 bg-white w-fit rounded-lg absolute bottom-2 right-2",
                  button: "bg-transparent px-2 py-1.5",
                  allowedContent: "hidden",
                }}
                content={{
                  button({ isUploading }) {
                    return (
                      <div className="flex items-center gap-1">
                        {isUploading ? <LoaderCircle className="size-4 animate-spin text-orange-500" /> : <Camera className="size-4 text-orange-500" />}
                        <span className="text-xs text-neutral-500">Image 4MB</span>
                      </div>
                    );
                  },
                }}
              />
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 p-2 md:grid-cols-2 md:p-4">
            <ClubInformations club={data} />
            <ClubMatchOffers matchOffers={data.matchOffers} clubRepresentativeId={data.clubRepresentativeId} />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="flex flex-col gap-4">
            <ClubTeams club={data} />
            <ClubSpecs club={data} />
          </div>

          <div className="flex flex-col gap-2">
            <ClubTournamentCard tournaments={data.tournaments} />
          </div>
        </div>
      </div>
    </>
  );
}
